import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSearch } from '../contexts/SearchContext';
import { useSearchResults } from '../hooks/useSearchResults';
import { useAuthUser } from '../contexts/AuthUserContext';
import { useCustomer } from '../contexts/CustomerContext';
import { useWeek } from '../contexts/WeekContext';
import { getSearchTabConfig, isTabSupported } from '../utils/searchTabConfig';
import { startOfDay, endOfDay, isWithinInterval } from 'date-fns';
import { parseCustomTimestamp } from '../utils/dateUtils';
import { 
  SearchResultsLayout, 
  BestMatchesTab, 
  CallsTab, 
  OrdersTab,
  TicketsTab,
  InventoryTab,
  AdminClientsTab,
  AdminActivityTab,
  AdminNotesTab,
  AdminBestMatchesTab
} from '../components/search';
import KronkChatTab from '../components/search/KronkChatTab';
import EmptyStateTab from '../components/search/EmptyStateTab';

const SearchResults = () => {
  const { searchQuery, activeTab, setActiveTab, viewMode } = useSearch();
  const { searchResults, totalResults, bestMatches, loading, error, errors: individualErrors } = useSearchResults(searchQuery);
  const { isHeliosAdmin } = useAuthUser();
  const { currentCustomer, isImpersonating } = useCustomer();
  const { dateRange } = useWeek();
  
  // Ensure non-admin users always see client tabs
  const effectiveViewMode = isHeliosAdmin ? viewMode : 'client';
  
  // Filter calls by date range for tab count
  const filteredCallsForCount = useMemo(() => {
    if (!searchResults.calls || searchResults.calls.length === 0) return [];
    if (!dateRange?.start || !dateRange?.end) return searchResults.calls;

    const rangeStart = startOfDay(dateRange.start);
    const rangeEnd = endOfDay(dateRange.end);

    return searchResults.calls.filter(call => {
      const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
      if (!callDate) return false;
      return isWithinInterval(callDate, { start: rangeStart, end: rangeEnd });
    });
  }, [searchResults.calls, dateRange]);
  
  const [selectedCall, setSelectedCall] = useState(null);
  
  // Animation states
  const [animationStates, setAnimationStates] = useState({
    header: false,
    tabs: false,
    results: false
  });

  // Trigger staggered animations on component mount
  useEffect(() => {
    const timeouts = [];

    // Stagger the animations with delays
    timeouts.push(setTimeout(() => {
      setAnimationStates(prev => ({ ...prev, header: true }));
    }, 100));

    timeouts.push(setTimeout(() => {
      setAnimationStates(prev => ({ ...prev, tabs: true }));
    }, 300));

    timeouts.push(setTimeout(() => {
      setAnimationStates(prev => ({ ...prev, results: true }));
    }, 500));

    // Cleanup timeouts on unmount
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Listen for page reset events from navbar
  useEffect(() => {
    const handlePageReset = (event) => {
      // Check if this page needs to reset based on its current state
      // If we have a selected call, reset to show only search results
      if (selectedCall !== null) {
        setSelectedCall(null);
      }
    };

    window.addEventListener('pageReset', handlePageReset);

    return () => {
      window.removeEventListener('pageReset', handlePageReset);
    };
  }, [selectedCall]);

  // Get counts for admin tabs - using state from component references
  const [adminCounts, setAdminCounts] = useState({
    bestMatchesCount: 0,
    clientsCount: 4, // Default client count
    activitiesCount: 0,
    notesCount: 0
  });
  
  // Reset admin counts when search query changes
  useEffect(() => {
    if (effectiveViewMode === 'admin') {
      // Only reset if search query is empty, otherwise let components update their own counts
      if (searchQuery.trim() === '') {
        setAdminCounts(prev => ({
          ...prev,
          bestMatchesCount: 0,
          activitiesCount: 0,
          notesCount: 0
        }));
      }
    }
  }, [searchQuery, effectiveViewMode]);

  // Pre-load admin data when in admin mode to ensure counts are always available
  useEffect(() => {
    if (effectiveViewMode === 'admin' && searchQuery.trim() !== '') {
      // All admin tabs are now always rendered, so counts will update automatically
      // No need for artificial count resets
    }
  }, [searchQuery, effectiveViewMode]);

  // Update admin counts when needed
  const updateAdminCount = (type, count) => {
    // Use a functional update to ensure we're working with the latest state
    setAdminCounts(prev => {
      // Only update if the count has actually changed
      if (prev[type] === count) {
        return prev; // No change needed
      }
      
      // Create new state object with updated count
      const newState = {
        ...prev,
        [type]: count,
        // Update best matches count whenever any count changes
        bestMatchesCount: Math.min(
          (type === 'clientsCount' ? count : prev.clientsCount) +
          (type === 'activitiesCount' ? count : prev.activitiesCount) +
          (type === 'notesCount' ? count : prev.notesCount),
          10 // Cap at 10 for best matches
        )
      };
      
      // Log the update for debugging
      // console.log(`Updating ${type} count from ${prev[type]} to ${count}`);
      
      return newState;
    });
  };

  // Expose count updater function to child components
  useEffect(() => {
    // Make the updateAdminCount function available globally for child components
    window.updateAdminCount = updateAdminCount;
    
    return () => {
      // Clean up when unmounting
      delete window.updateAdminCount;
    };
  }, []);
  
  // Get customer-specific tab configuration
  const getCustomerTabConfig = () => {
    if (effectiveViewMode === 'admin') {
      // Admin tabs with dynamic counts from state
      return [
        {
          id: 'best-match',
          label: 'Best Match',
          icon: 'material-symbols:star',
          count: adminCounts.bestMatchesCount
        },
        {
          id: 'clients',
          label: 'Clients',
          icon: 'fluent:organization-16-filled',
          count: adminCounts.clientsCount
        },
        {
          id: 'activity',
          label: 'Activity',
          icon: 'hugeicons:activity-04',
          count: adminCounts.activitiesCount
        },
        {
          id: 'notes',
          label: 'Andy Notes',
          icon: 'material-symbols:note-alt-rounded',
          count: adminCounts.notesCount
        }
      ];
    } else {
      // Client view tabs
      // When not impersonating (admin view), show only Tickets, Best Match, and Calls
      // When impersonating, show customer-specific tabs
      let customerConfig;
      
      // Check if admin is not impersonating - use simplified client view
      // When admin is not impersonating, they should see simplified client tabs (Best Match, Tickets, Calls)
      // This applies regardless of what currentCustomer?.id is (helios, celeste, etc.)
      if (isHeliosAdmin && !isImpersonating) {
        // Admin not impersonating - show simplified client view with 3 tabs
        customerConfig = {
          tabs: [
            {
              id: 'best-match',
              label: 'Best Match',
              icon: 'material-symbols:star',
              count: 0
            },
            {
              id: 'tickets',
              label: 'Tickets',
              icon: 'material-symbols:confirmation-number',
              count: 0
            },
            {
              id: 'calls',
              label: 'Calls',
              icon: 'material-symbols-light:call-log',
              count: 0
            }
          ]
        };
      } else {
        // Get customer-specific tab configuration when impersonating or for regular users
        const customerId = currentCustomer?.id || 'default';
        customerConfig = getSearchTabConfig(customerId);
      }
      
      // Update counts based on search results
      const updatedTabs = customerConfig.tabs.map(tab => {
        let count = 0;
        
        switch (tab.id) {
          case 'best-match':
            count = bestMatches.length;
            break;
          case 'tickets':
            count = searchResults.tickets.length;
            break;
          case 'calls':
            count = filteredCallsForCount.length;
            break;
          case 'orders':
            // For kronk, use shopifyOrders; for others, use regular orders
            const customerIdForCount = currentCustomer?.id || 'default';
            count = customerIdForCount === 'kronk' 
              ? searchResults.shopifyOrders?.length || 0
              : searchResults.orders.length;
            break;
          case 'inventory':
            count = searchResults.inventory.length;
            break;
          case 'chat-history':
            count = filteredCallsForCount.length; // Use filtered calls data for chat history
            break;
          case 'insights':
            count = filteredCallsForCount.length; // Use filtered calls data for insights
            break;
          default:
            count = 0;
        }
        
        return { ...tab, count };
      });

      // Debug logging for customer-specific tab config
      // console.log('Customer tab config updated:', {
      //   customerId,
      //   isImpersonating,
      //   searchQuery: searchQuery || '(empty)',
      //   tabs: updatedTabs,
      //   searchResultsKeys: Object.keys(searchResults)
      // });

      return updatedTabs;
    }
  };

  // Create memoized tabConfig that updates when relevant state changes
  // Include searchResults object to ensure counts are calculated from the same data that's rendered
  const tabConfig = React.useMemo(() => {
    return getCustomerTabConfig();
  }, [effectiveViewMode, adminCounts, bestMatches, searchResults, currentCustomer?.id, isImpersonating, isHeliosAdmin, searchQuery, filteredCallsForCount]);

  // Render tab content based on active tab and view mode
  const renderTabContent = () => {
    // Only show loading if we're still loading AND have no data at all
    if (loading && !searchResults.calls.length && !searchResults.tickets.length && !searchResults.orders.length && !searchResults.inventory.length) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon icon="eos-icons:loading" className="text-4xl text-primary animate-spin mb-4" />
            <p className="text-font-secondary">Searching...</p>
          </div>
        </div>
      );
    }

    if (searchQuery.trim() === '') {
      return (
        <div className="flex items-center justify-center h-full w-full">
          <div className="flex flex-row gap-4 items-center">
            <Icon icon="material-symbols:search" className="text-6xl text-bgAccent" />
            <p className="text-font-secondary text-lg">Enter a search term to find results.</p>
          </div>
        </div>
      );
    }

    if (effectiveViewMode === 'admin') {
      // Admin view tabs - always render all tabs to ensure counts are updated
      // but only show the active one
      const adminTabs = (
        <>
          <div className={activeTab === 'clients' ? 'block w-full' : 'hidden'}>
            <AdminClientsTab searchQuery={searchQuery} />
          </div>
          <div className={activeTab === 'activity' ? 'block w-full' : 'hidden'}>
            <AdminActivityTab searchQuery={searchQuery} />
          </div>
          <div className={activeTab === 'notes' ? 'block w-full' : 'hidden'}>
            <AdminNotesTab searchQuery={searchQuery} />
          </div>
          <div className={activeTab === 'best-match' ? 'block w-full' : 'hidden'}>
            <div className="flex flex-col gap-4 w-full">
              {/* Admin Best Matches - Show actual content from all admin sources */}
              <AdminBestMatchesTab 
                searchQuery={searchQuery}
                adminCounts={adminCounts}
              />
            </div>
          </div>
        </>
      );
      
      return adminTabs;
    } else {
      // Client view tabs (default)
      if (totalResults === 0) {
        return (
          <div className="flex items-center justify-center h-full w-full">
            <div className="flex flex-row gap-4 items-center">
              <Icon icon="material-symbols:search-off" className="text-8xl text-font-secondary opacity-50" />
              <div className="flex flex-col gap-2 items-start">
                <p className="text-font-secondary text-lg">No results found for "{searchQuery}"</p>
                <p className="text-font-secondary">Try using different keywords or check your spelling</p>
              </div>
            </div>
          </div>
        );
      }

      // Get current customer configuration
      const customerId = currentCustomer?.id || 'default';
      const customerConfig = getSearchTabConfig(customerId);
      
      // Check if the active tab is supported for this customer
      // Skip this check when admin is not impersonating, since we use a simplified tab config
      if (!(isHeliosAdmin && !isImpersonating) && !isTabSupported(customerId, activeTab)) {
        const tabLabel = customerConfig.tabs.find(tab => tab.id === activeTab)?.label || activeTab;
        return <EmptyStateTab tabLabel={tabLabel} searchQuery={searchQuery} />;
      }

      switch (activeTab) {
        case 'best-match':
          return (
            <BestMatchesTab
              bestMatches={bestMatches}
              selectedCall={selectedCall}
              setSelectedCall={setSelectedCall}
            />
          );
        case 'tickets':
          // Show error only for tickets tab if tickets data failed
          if (individualErrors?.tickets && !searchResults.tickets.length) {
            return (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Icon icon="material-symbols:error-outline" className="text-4xl text-error mb-4" />
                  <p className="text-error">Error loading tickets: {individualErrors.tickets.message || individualErrors.tickets}</p>
                </div>
              </div>
            );
          }
          console.log('Rendering TicketsTab with:', { tickets: searchResults.tickets, ticketsLength: searchResults.tickets?.length });
          return (
            <TicketsTab
              tickets={searchResults.tickets}
              searchQuery={searchQuery}
            />
          );
        case 'calls':
          // Show error only for calls tab if calls data failed
          if (individualErrors?.calls && !searchResults.calls.length) {
            return (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Icon icon="material-symbols:error-outline" className="text-4xl text-error mb-4" />
                  <p className="text-error">Error loading calls: {individualErrors.calls.message || individualErrors.calls}</p>
                </div>
              </div>
            );
          }
          console.log('Rendering CallsTab with:', { calls: searchResults.calls, callsLength: searchResults.calls?.length });
          return (
            <CallsTab
              calls={searchResults.calls}
              searchQuery={searchQuery}
              selectedCall={selectedCall}
              setSelectedCall={setSelectedCall}
            />
          );
        case 'orders':
          // Determine which orders to show based on customer
          const customerIdForOrders = currentCustomer?.id || 'default';
          const ordersToShow = customerIdForOrders === 'kronk' 
            ? searchResults.shopifyOrders || []
            : searchResults.orders || [];
          
          // Show error only for orders tab if orders data failed
          const ordersError = customerIdForOrders === 'kronk' 
            ? individualErrors?.shopifyOrders 
            : individualErrors?.orders;
          
          if (ordersError && ordersToShow.length === 0) {
            return (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Icon icon="material-symbols:error-outline" className="text-4xl text-error mb-4" />
                  <p className="text-error">Error loading orders: {ordersError.message || ordersError}</p>
                </div>
              </div>
            );
          }
          return (
            <OrdersTab
              orders={ordersToShow}
              searchQuery={searchQuery}
              selectedCall={selectedCall}
              setSelectedCall={setSelectedCall}
            />
          );
        case 'inventory':
          // Show error only for inventory tab if inventory data failed
          if (individualErrors?.inventory && !searchResults.inventory.length) {
            return (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Icon icon="material-symbols:error-outline" className="text-4xl text-error mb-4" />
                  <p className="text-error">Error loading inventory: {individualErrors.inventory.message || individualErrors.inventory}</p>
                </div>
              </div>
            );
          }
          return (
            <InventoryTab
              inventory={searchResults.inventory}
              searchQuery={searchQuery}
            />
          );
        case 'chat-history':
          // Show error only for chat-history tab if calls data failed (chats use callData)
          if (individualErrors?.calls && !searchResults.calls.length) {
            return (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Icon icon="material-symbols:error-outline" className="text-4xl text-error mb-4" />
                  <p className="text-error">Error loading chat history: {individualErrors.calls.message || individualErrors.calls}</p>
                </div>
              </div>
            );
          }
          return (
            <KronkChatTab
              searchQuery={searchQuery}
            />
          );
        case 'insights':
          // For now, show empty state for insights - can be implemented later
          return <EmptyStateTab tabLabel="Insights" searchQuery={searchQuery} />;
        default:
          // Best match tab - show errors only if ALL data sources failed
          const allDataSourcesFailed = individualErrors?.calls && individualErrors?.tickets && 
            (!searchResults.calls.length && !searchResults.tickets.length && !searchResults.orders.length);
          
          if (allDataSourcesFailed && !bestMatches.length) {
            return (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Icon icon="material-symbols:error-outline" className="text-4xl text-error mb-4" />
                  <p className="text-error">Error loading search results. Please try again.</p>
                </div>
              </div>
            );
          }
          
          return (
            <BestMatchesTab
              bestMatches={bestMatches}
              selectedCall={selectedCall}
              setSelectedCall={setSelectedCall}
            />
          );
      }
    }
  };

  // Calculate total results for admin view
  const getAdminTotalResults = () => {
    if (effectiveViewMode !== 'admin') return totalResults;
    
    return adminCounts.clientsCount + adminCounts.activitiesCount + adminCounts.notesCount;
  };
  
  const displayTotalResults = effectiveViewMode === 'admin' ? getAdminTotalResults() : totalResults;
  
  // Get customer configuration for SearchResultsLayout
  const getCustomerConfig = () => {
    if (effectiveViewMode === 'admin') {
      return null; // Admin mode doesn't need customer filtering
    }
    
    // When admin is not impersonating, return simplified config with Best Match, Tickets, and Calls
    if (isHeliosAdmin && !isImpersonating) {
      return {
        tabs: [
          { id: 'best-match' },
          { id: 'tickets' },
          { id: 'calls' }
        ]
      };
    }
    
    // When impersonating or for regular users, use customer-specific config
    const customerId = currentCustomer?.id || 'default';
    return getSearchTabConfig(customerId);
  };

  return (
    <SearchResultsLayout
      searchQuery={searchQuery}
      totalResults={displayTotalResults}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabConfig={tabConfig}
      animationStates={animationStates}
      customerConfig={getCustomerConfig()}
    >
      {renderTabContent()}
    </SearchResultsLayout>
  );
};

export default SearchResults;
